import 'dayjs/locale/es';

import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { InvoiceIcon } from '../assets';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { TableSearchInput } from '../components/inputs/TableSearchInput';
import { SubrowTable } from '../components/invoices/SubrowsTable';
import DataTable from '../components/tables/DataTable';
import type { ExtraInfoCampaignHeader } from '../components/tables/headers/campaigns';
import { getCampaignInvoicesTableHeaders } from '../components/tables/headers/invoices';
import { replaceIds, ROUTES } from '../config/routes/config';
import { GET_CAMPAIGN_INVOICES_LIST } from '../graphql/campaign/query';
import { usePermissions } from '../hooks/usePermissions';
import { useTableDataAndFilters } from '../hooks/useTableDataAndFilters';
import { userAtom } from '../states/atoms';
import type { CampaignInvoicesTable, ResCampaignInvoicesList } from '../types';

dayjs.extend(localizedFormat);
dayjs.locale('es');

function Invoices() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { isInfluencer, isBrand } = usePermissions();
  const location = useLocation();
  const { id: userId } = useRecoilValue(userAtom);

  // Extract the status from the URL query parameters
  const params = new URLSearchParams(location.search);

  /**
   * Hook in charge of the logic of the data, filtering and pagination
   */
  const {
    data: campaigns,
    total,
    error,
    loading,
    filters,
    table,
    resetPagination,
    setFilters,
  } = useTableDataAndFilters<
    any,
    CampaignInvoicesTable,
    ExtraInfoCampaignHeader,
    ResCampaignInvoicesList
  >({
    getColumns: getCampaignInvoicesTableHeaders,
    columnsExtras: { isInfluencer: isInfluencer() },
    responseAccessKey: 'campaignInvoices',
    query: GET_CAMPAIGN_INVOICES_LIST,
    subrowAccesor: 'users',
    defaultExpanded: true,
  });

  /**
   * Handles click on any row of the table and navigates to single campaign
   * @param id id of the row clicked
   */
  const handleTableClick = (id: string, influencer_id?: string) => {
    const campaign = campaigns.find((c) => c.id === id);
    if (campaign) {
      let invoiceId = influencer_id ? 'influencers' : campaign.brand.id;
      if (isInfluencer()) invoiceId = 'influencers';
      if (isBrand()) invoiceId = campaign.brand.id;

      navigate(
        replaceIds({
          url: ROUTES.SINGLE_CAMPAIGN_INVOICES,
          params: [
            {
              campaignId: campaign.id,
            },
            {
              id: invoiceId,
            },
          ],
        }),
      );
    }
  };

  return (
    <div className="w-full flex flex-col gap-y-5">
      <Breadcrumbs
        icon={<InvoiceIcon />}
        breadcrumbs={[
          {
            text: t('invoices.title'),
          },
        ]}
      />
      <div className="flex flex-col gap-y-4">
        <div className="flex flex-row justify-between">
          <div>
            <TableSearchInput
              placeholder={t('invoices.searchInputPlaceholder')}
              filters={{ ...filters }}
              setFilters={(val) => {
                setFilters(val);
                resetPagination();
              }}
              searchKey="campaignName"
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-y-4">
        <DataTable
          error={error}
          handleRowClick={(id) => handleTableClick(id)}
          handleSubrowClick={(id, influencer_id) => {
            handleTableClick(id, influencer_id);
          }}
          loading={loading}
          table={table}
          total={total}
          tableId="CampaignsTable"
          sorting={{ ...filters }}
          SubrowsComponent={SubrowTable}
        />
      </div>
    </div>
  );
}

export default Invoices;
